import { format } from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';
import { BookingStepsContextState } from '../../context/bookingContext/bookingContext.types';
import {
  AdditionalDataEnum,
  BookingConfig,
  BookingConfigEnum,
  BookingStepEnum,
  BookingStepValueEnum,
  BookingSubStepValueEnum,
} from '../../components/BookingWidget/bookingSteps.interface';
import {
  BookingQuoteBeneficiaries,
  BookingQuoteBeneficiariesEnum,
} from '../../components/BookingWidget/BookingQuoteManagment/bookingQuote.interface';
import {
  FormQuotationInformationEnum,
  FormQuotationInformationPersonalData,
  FormQuotationInformationTraveller,
  FormQuotationInformationTravellerEnum,
  FormQuotationInformationTravellerList,
} from '../../components/QuotationInformationForm/quoation-information.interface';
import { getBirthdateForTraveller } from './getTravellersAge';
import parseStringToDate from './date.helper';
import { ApiDateFormat } from '../consts/app.const';
import { BookingDataResponse } from '../../components/BookingWidget/BookingManagment/bookingManagment.interface';
import getBookingStepIndex from './getBookingStepIndex.helper';

const ageString = 'travellerAge';
const firstNameString = 'travellerFirstName';
const lastNameString = 'travellerLastName';

const getBeneficiariesArray = (
  bookingStepsContext: BookingStepsContextState,
  bookingConfig: BookingConfig,
  bookingDataResponse: BookingDataResponse | null = null,
  options: {
    inferDateOfBirth?: boolean;
    skipAge?: boolean;
  } = {
    inferDateOfBirth: false,
    skipAge: false,
  },
): BookingQuoteBeneficiaries[] => {
  const { inferDateOfBirth, skipAge } = options;
  const quotationInformation =
    bookingStepsContext[BookingStepValueEnum.QuotationInformation];
  const quotationForm = bookingStepsContext[BookingStepValueEnum.QuotationForm];
  const travellersList =
    quotationInformation[BookingSubStepValueEnum.InformationTravellers];
  const informationPersonal =
    quotationInformation[BookingSubStepValueEnum.InformationPersonal];
  const travellersAge = quotationForm[BookingSubStepValueEnum.TravellersAge];

  const companions = bookingDataResponse?.companions;
  const isPolicyHolderTravellingValue =
    bookingStepsContext[BookingStepValueEnum.QuotationInformation][
      BookingSubStepValueEnum.IsPolicyHolderTravelling
    ];

  if (
    (!informationPersonal ||
      !informationPersonal[FormQuotationInformationEnum.Birthdate]) &&
    travellersList === null
  ) {
    return travellersAge.map((t, i) => {
      const dob =
        getBirthdateForTraveller(t.dateOfBirth as Dayjs) ||
        (inferDateOfBirth
          ? getBirthdateForTraveller(travellersAge[i]?.age)
          : null);
      return {
        ...(companions &&
          companions[i]?.id && {
            [BookingQuoteBeneficiariesEnum.IdNumber]: companions[i].id,
          }),
        ...(!skipAge && {
          [BookingQuoteBeneficiariesEnum.Age]: Number(t.age),
        }),
        ...(dob && {
          birthDate: dob,
        }),
      };
    });
  }
  const beneficiaries: BookingQuoteBeneficiaries[] = [];

  const QuotationInformationStepIndex = getBookingStepIndex(
    bookingConfig,
    BookingStepValueEnum.QuotationInformation,
  );

  const isPolicyHolderAsFirstTraveller =
    isPolicyHolderTravellingValue ||
    (bookingConfig[BookingConfigEnum.BookingSteps][
      QuotationInformationStepIndex
    ][BookingStepEnum.AdditionalData]?.[
      AdditionalDataEnum.PolicyHolderIsTraveller
    ] ??
      true);

  const hasRequiredField = (
    field: keyof FormQuotationInformationPersonalData,
  ): boolean => !!informationPersonal[field];

  const getFieldValueOrDefault = (
    fieldType:
      | typeof firstNameString
      | typeof lastNameString
      | typeof ageString,
    index: number,
  ): string => {
    const fieldKey: keyof FormQuotationInformationTravellerList =
      `${fieldType}${index}` as keyof FormQuotationInformationTravellerList;
    return travellersList[fieldKey] || '';
  };

  let firstTravellerAge = null;
  const isFirstTravellerBirthdateFromInformationPersonal =
    !!isPolicyHolderTravellingValue &&
    !!informationPersonal[FormQuotationInformationEnum.Birthdate];
  const isFirstTravellerBirthdateFromQuotationForm =
    !!quotationForm[BookingSubStepValueEnum.TravellersAge][0].dateOfBirth;

  let firstTravellerBirthdateSource;
  if (isFirstTravellerBirthdateFromInformationPersonal) {
    firstTravellerBirthdateSource = parseStringToDate(
      informationPersonal[FormQuotationInformationEnum.Birthdate],
    );
  } else if (isFirstTravellerBirthdateFromQuotationForm) {
    firstTravellerBirthdateSource = (
      quotationForm[BookingSubStepValueEnum.TravellersAge][0]
        .dateOfBirth as Dayjs
    ).toDate();
  } else {
    firstTravellerBirthdateSource = null;
  }

  let firstTravellerBirthdate = firstTravellerBirthdateSource
    ? format(firstTravellerBirthdateSource, ApiDateFormat)
    : null;

  if (!inferDateOfBirth && !firstTravellerBirthdate) {
    firstTravellerAge = Number(
      quotationForm[BookingSubStepValueEnum.TravellersAge][0].age,
    );
  }

  if (inferDateOfBirth && !firstTravellerBirthdate) {
    firstTravellerBirthdate = getBirthdateForTraveller(
      travellersList[
        `${ageString}${0}` as keyof FormQuotationInformationTravellerList
      ],
    );
  }

  let i = 1;
  beneficiaries.push({
    ...(firstTravellerBirthdate && {
      [BookingQuoteBeneficiariesEnum.BirthDate]: firstTravellerBirthdate,
    }),
    ...(firstTravellerAge &&
      firstTravellerAge !== null && {
        [BookingQuoteBeneficiariesEnum.Age]: firstTravellerAge,
      }),
    [BookingQuoteBeneficiariesEnum.Firstname]:
      isPolicyHolderAsFirstTraveller &&
      hasRequiredField(FormQuotationInformationEnum.Firstname)
        ? informationPersonal[FormQuotationInformationEnum.Firstname]
        : getFieldValueOrDefault(firstNameString, 0),
    [BookingQuoteBeneficiariesEnum.Lastname]:
      isPolicyHolderAsFirstTraveller &&
      hasRequiredField(FormQuotationInformationEnum.Lastname)
        ? informationPersonal[FormQuotationInformationEnum.Lastname]
        : getFieldValueOrDefault(lastNameString, 0),
    [BookingQuoteBeneficiariesEnum.Subscriber]:
      !!isPolicyHolderAsFirstTraveller,
    ...(companions &&
      companions[0].id && {
        [BookingQuoteBeneficiariesEnum.IdNumber]: companions[0].id,
      }),
  });
  while (i < 11) {
    const keyAge: string = `${ageString}${i}`.toString();
    const keyFirstName: string = `${firstNameString}${i}`.toString();
    const keyLastName: string = `${lastNameString}${i}`.toString();
    if (
      travellersList[keyAge as keyof FormQuotationInformationTravellerList] &&
      travellersAge[i]
    ) {
      let age = null;
      let birthDate = travellersAge[i]?.dateOfBirth
        ? format(
            (travellersAge[i].dateOfBirth as Dayjs)?.toDate(),
            ApiDateFormat,
          )
        : null;
      if (!inferDateOfBirth && !birthDate && travellersAge[i].age) {
        age = Number(travellersAge[i].age);
      }
      if (inferDateOfBirth && !birthDate) {
        birthDate = getBirthdateForTraveller(
          travellersList[
            `${ageString}${i}` as keyof FormQuotationInformationTravellerList
          ],
        );
      }

      beneficiaries.push({
        ...(birthDate && {
          [BookingQuoteBeneficiariesEnum.BirthDate]: birthDate,
        }),
        ...(age &&
          age !== null && {
            [BookingQuoteBeneficiariesEnum.Age]: Number(travellersAge[i].age),
          }),
        [BookingQuoteBeneficiariesEnum.Firstname]: getFieldValueOrDefault(
          firstNameString,
          i,
        ),
        [BookingQuoteBeneficiariesEnum.Lastname]: getFieldValueOrDefault(
          lastNameString,
          i,
        ),
        [BookingQuoteBeneficiariesEnum.Subscriber]: false,
        ...(companions &&
          companions[i]?.id && {
            [BookingQuoteBeneficiariesEnum.IdNumber]: companions[i]?.id,
          }),
      });
    } else if (travellersAge[i]) {
      beneficiaries.push({
        ...travellersAge[i],
        ...(companions &&
          companions[i]?.id && {
            [BookingQuoteBeneficiariesEnum.IdNumber]: companions[i].id,
          }),
        [BookingQuoteBeneficiariesEnum.Age]: Number(travellersAge[i].age),
      });
    }
    // eslint-disable-next-line no-plusplus
    i++;
  }
  return beneficiaries;
};

export const getBeneficiariesListFromTravellersArray = (
  travelers: FormQuotationInformationTraveller[],
  travellerList: FormQuotationInformationTravellerList,
  policyHolderIsTraveller: boolean,
): FormQuotationInformationTravellerList => {
  const newTravellerList: FormQuotationInformationTravellerList = {};
  travelers.forEach(
    (traveller: FormQuotationInformationTraveller, index: number) => {
      // Only process if we have valid date
      if (
        traveller[FormQuotationInformationTravellerEnum.BirthDate] &&
        !dayjs(
          traveller[FormQuotationInformationTravellerEnum.BirthDate],
        ).isValid()
      ) {
        return; // Skip invalid dates
      }
      const travellerIndex = policyHolderIsTraveller ? index + 1 : index;
      newTravellerList[
        `travellerFirstName${travellerIndex}` as keyof FormQuotationInformationTravellerList
      ] = traveller[FormQuotationInformationTravellerEnum.Firstname];
      newTravellerList[
        `travellerLastName${travellerIndex}` as keyof FormQuotationInformationTravellerList
      ] = traveller[FormQuotationInformationTravellerEnum.Lastname];
      if (traveller[FormQuotationInformationTravellerEnum.BirthDate]) {
        newTravellerList[
          `travellerAge${travellerIndex}` as keyof FormQuotationInformationTravellerList
        ] = traveller[FormQuotationInformationTravellerEnum.BirthDate];
      } else {
        newTravellerList[
          `travellerAge${travellerIndex}` as keyof FormQuotationInformationTravellerList
        ] = traveller[FormQuotationInformationTravellerEnum.Age];
      }
    },
  );
  return { ...travellerList, ...newTravellerList };
};

export default getBeneficiariesArray;
